import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PageProfile from './PageProfile';
import { Loader2 } from 'lucide-react';

const ProfileRedirect: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) {
        // If not logged in, redirect to login page
        navigate('/login');
        return;
      }

      if (!supabase) {
        console.error('Supabase not configured');
        navigate('/dashboard');
        return;
      }

      try {
        // Get the username of the logged-in user
        const { data: userProfile, error } = await supabase
          .from('user_profiles')
          .select('username')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching profile:', error);
          // If RLS error or other, redirect to dashboard instead of blocking
          navigate('/dashboard');
          return;
        }

        if (userProfile?.username) {
          // If user has a username, redirect to profile page with username
          navigate(`/profiles/${userProfile.username}`, { replace: true });
          return;
        }

        // If no username, display profile page directly for logged-in user
        setLoading(false);
      } catch (err) {
        console.error('Error loading profile:', err);
        // In case of error, still display profile page for logged-in user
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[rgb(var(--brand))] mx-auto mb-4" />
          <p className="text-gray-600 dark:text-[rgb(var(--muted))]">
            Loading your profile...
          </p>
        </div>
      </div>
    );
  }

  // Display PageProfile directly with the logged-in user's user_id
  // By passing null as username, we indicate to PageProfile to use the logged-in user
  return <PageProfile userId={user?.id} />;
};

export default ProfileRedirect;
